import type { CraneLoadRig, HazardsConfig } from '../config/hazards.config';
import type { WorldConfig } from '../config/world.config';
import { laneCenterX } from '../level/segments/segmentBuilders';
import { clamp, DEG_TO_RAD, lerp, smoothstep, TAU } from '../utils/math';
import { HazardKind, type CraneBeamHazard, type CraneBlockHazard, type CraneSwingHazard } from './HazardDefinition';
import { colliderSlot, HazardCue, HazardPhase, setBox, type HazardCollider, type HazardModel, type HazardStepContext } from './HazardModel';

/** Crane load pose: hoist height of the load's underside and the pendulum of the load on its rope. */
export type CraneLoadPose = {
  /** Underside of the load at the rest angle (height above the road). */
  hoist: number;
  /** Pendulum angle about the rope top (radians, positive = load swings towards +z). */
  swing: number;
  /** Turn of the load about its rope (radians). */
  spin: number;
};

const craneLoadPose = (hoist: number): CraneLoadPose => ({ hoist, swing: 0, spin: 0 });

/**
 * OBS_CRANE_LOAD_A – two steel beams across all lanes. Hoisted high while idle; after the
 * warning the crane lowers them to slide height, where they swing along the track. The swing is
 * phase-locked to the runner's arrival: the beams pass their lowest point when the runner gets
 * there, so the slide is always readable. Standing (or jumping) into them is a hit.
 *
 * Colliders: [0] the beams across every lane, [1] the sling above them – only over the centre lane,
 * where its legs and hook close below a runner's head (rig.guardHalfWidth). Over the outer lanes the
 * beam ends lie under open sky: a double jump clears them.
 */
export class CraneBeamModel implements HazardModel<CraneLoadPose> {
  readonly kind = HazardKind.CraneBeam;
  phase: HazardPhase = HazardPhase.Idle;
  readonly pose: CraneLoadPose;
  readonly previous: CraneLoadPose;
  readonly colliders = [colliderSlot(), colliderSlot()];
  private time = 0;
  private phaseTime = 0;
  private swingClock = 0;

  /** Seconds before the runner's arrival at which the warning starts. */
  static startLead(config: HazardsConfig['craneBeam']): number {
    return config.telegraph + config.lowerDuration + config.readyLead;
  }

  constructor(
    private readonly spec: CraneBeamHazard,
    private readonly config: HazardsConfig['craneBeam'],
  ) {
    this.pose = craneLoadPose(config.hoistedClearance);
    this.previous = craneLoadPose(config.hoistedClearance);
    this.updateCollider();
  }

  step(dt: number, ctx: HazardStepContext, cues: HazardCue[]): void {
    Object.assign(this.previous, this.pose);
    const c = this.config;
    this.time += dt;
    switch (this.phase) {
      case HazardPhase.Idle:
        if (ctx.armed && ctx.eta(this.spec.z) <= CraneBeamModel.startLead(c)) {
          this.phase = HazardPhase.Warning;
          this.phaseTime = 0;
          cues.push(HazardCue.Warning);
        }
        break;
      case HazardPhase.Warning:
        this.phaseTime += dt;
        if (this.phaseTime >= c.telegraph) {
          this.phase = HazardPhase.Active;
          this.phaseTime = 0;
          // Swing clock 0 = runner arrives: the beams pass the bottom of the swing right then.
          this.swingClock = -ctx.eta(this.spec.z);
        }
        break;
      case HazardPhase.Active:
        this.phaseTime += dt;
        this.swingClock += dt;
        break;
    }
    const lowered = this.phase === HazardPhase.Active ? smoothstep(this.phaseTime / c.lowerDuration) : 0;
    const idleSway = c.idleSwayDegrees * DEG_TO_RAD * Math.sin((TAU * this.time) / c.idleSwayPeriod);
    const swing = c.swingDegrees * DEG_TO_RAD * Math.sin((TAU * this.swingClock) / c.swingPeriod);
    this.pose.hoist = lerp(c.hoistedClearance, this.spec.clearance, lowered);
    this.pose.swing = lerp(idleSway, swing, lowered);
    this.updateCollider();
  }

  /** Beams rotated about the rope top, plus the sling above them over the centre (no jumping over there). */
  private updateCollider(): void {
    const { rig } = this.config;
    const pivotY = this.pose.hoist - rig.loadBottom + rig.pivotHeight;
    const radius = rig.pivotHeight - rig.loadBottom - rig.load.height / 2;
    const angle = this.pose.swing;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const halfHeight = (rig.load.height / 2) * Math.abs(cos) + (rig.load.depth / 2) * Math.abs(sin);
    const halfDepth = (rig.load.height / 2) * Math.abs(sin) + (rig.load.depth / 2) * Math.abs(cos);
    const centerY = pivotY - radius * cos;
    const centerZ = this.spec.z + radius * sin;
    const halfWidth = rig.load.width / 2;
    const slingHalfWidth = Math.min(halfWidth, rig.guardHalfWidth ?? halfWidth);
    const top = centerY + halfHeight;
    setBox(this.colliders[0]!, -halfWidth, halfWidth, centerY - halfHeight, top, centerZ - halfDepth, centerZ + halfDepth);
    setBox(this.colliders[1]!, -slingHalfWidth, slingHalfWidth, top, top + rig.guardHeight, centerZ - halfDepth, centerZ + halfDepth);
  }
}

/**
 * OBS_CRANE_LOAD_B – a precast concrete block on a chain sling, hoisted over one lane and
 * slowly turning. After the warning (horn, beacons) it drops to just above the road and
 * stays there: a lane block that must be dodged. It is down `readyLead` seconds before the
 * runner arrives.
 */
export class CraneBlockModel implements HazardModel<CraneLoadPose> {
  readonly kind = HazardKind.CraneBlock;
  phase: HazardPhase = HazardPhase.Idle;
  readonly pose: CraneLoadPose;
  readonly previous: CraneLoadPose;
  readonly colliders = [colliderSlot()];
  private readonly x: number;
  private time = 0;
  private phaseTime = 0;
  private landed = false;
  /** Turn when the drop started – it eases to the nearest aligned position while dropping. */
  private spinAtDrop = 0;

  static startLead(config: HazardsConfig['craneBlock']): number {
    return config.telegraph + config.dropDuration + config.readyLead;
  }

  constructor(
    private readonly spec: CraneBlockHazard,
    private readonly config: HazardsConfig['craneBlock'],
    world: WorldConfig,
  ) {
    this.x = laneCenterX(world, spec.lane);
    this.pose = craneLoadPose(config.hoistedClearance);
    this.previous = craneLoadPose(config.hoistedClearance);
    this.updateCollider();
  }

  step(dt: number, ctx: HazardStepContext, cues: HazardCue[]): void {
    Object.assign(this.previous, this.pose);
    const c = this.config;
    this.time += dt;
    const sway = c.idleSwayDegrees * DEG_TO_RAD * Math.sin((TAU * this.time) / c.idleSwayPeriod);
    switch (this.phase) {
      case HazardPhase.Idle:
      case HazardPhase.Warning:
        this.pose.spin += c.idleSpinDegreesPerSecond * DEG_TO_RAD * dt;
        this.pose.swing = sway;
        if (this.phase === HazardPhase.Idle && ctx.armed && ctx.eta(this.spec.z) <= CraneBlockModel.startLead(c)) {
          this.phase = HazardPhase.Warning;
          this.phaseTime = 0;
          cues.push(HazardCue.Warning);
        } else if (this.phase === HazardPhase.Warning) {
          this.phaseTime += dt;
          if (this.phaseTime >= c.telegraph) {
            this.phase = HazardPhase.Active;
            this.phaseTime = 0;
            this.spinAtDrop = this.pose.spin;
          }
        }
        break;
      case HazardPhase.Active: {
        this.phaseTime += dt;
        const t = Math.min(1, this.phaseTime / c.dropDuration);
        // Released brake: the load accelerates downwards and turns into line with the lane.
        this.pose.hoist = lerp(c.hoistedClearance, c.loweredClearance, t * t);
        this.pose.spin = lerp(this.spinAtDrop, Math.round(this.spinAtDrop / Math.PI) * Math.PI, smoothstep(t));
        this.pose.swing = sway * (1 - t);
        if (t >= 1) {
          if (!this.landed) {
            this.landed = true;
            cues.push(HazardCue.Impact);
          }
          const bounce = (this.phaseTime - c.dropDuration) / c.bounceDuration;
          this.pose.hoist = c.loweredClearance + (bounce < 1 ? c.bounceHeight * Math.sin(Math.PI * bounce) : 0);
        }
        break;
      }
    }
    this.updateCollider();
  }

  private updateCollider(): void {
    const { rig } = this.config;
    const cos = Math.abs(Math.cos(this.pose.spin));
    const sin = Math.abs(Math.sin(this.pose.spin));
    const halfX = (rig.load.width / 2) * cos + (rig.load.depth / 2) * sin;
    const halfZ = (rig.load.width / 2) * sin + (rig.load.depth / 2) * cos;
    const bottom = this.pose.hoist;
    setBox(this.colliders[0]!, this.x - halfX, this.x + halfX, bottom, bottom + rig.load.height + rig.guardHeight, this.spec.z - halfZ, this.spec.z + halfZ);
  }
}

// ── Swinging block (pendulum across the lanes) ──────────────────────────────

/** Pose of the swinging block: the crane load pose plus the lateral pendulum and its rope top. */
export type CraneSwingPose = CraneLoadPose & {
  /** Lateral pendulum angle about the rope top (radians, positive = load swings towards +x). */
  lateral: number;
  /** Rope top (hook block, trolley) beside the track centre (m, +x). */
  pivotX: number;
};

const craneSwingPose = (hoist: number): CraneSwingPose => ({ hoist, swing: 0, spin: 0, lateral: 0, pivotX: 0 });

/** Rope top → centre of the load (pendulum length). */
export function craneSwingRadius(rig: CraneLoadRig): number {
  return rig.pivotHeight - rig.loadBottom - rig.load.height / 2;
}

/**
 * Phase of the lateral swing at the runner's arrival (load centre x = reach · sin(phase)): over an outer
 * lane the turning point (±π/2), over the centre lane the sweep through it (0 = towards +x, π = towards −x).
 */
export function craneSwingArrivalPhase(laneX: number, sweep: 1 | -1 = 1): number {
  const side = Math.abs(laneX) < 1e-6 ? 0 : Math.sign(laneX);
  if (side !== 0) return (side * Math.PI) / 2;
  return sweep > 0 ? 0 : Math.PI;
}

/** Rope angle and rope top of the swing. */
export interface CraneSwingLateral {
  angle: number;
  pivotX: number;
}

/**
 * The full swing at swing clock `clock` (s relative to the runner's arrival): the load's centre moves on a
 * sine across the track, x = reach · sin(2π·clock / period + arrivalPhase) – slow at the turning points, fast
 * over the centre lane. The trolley carries `trolleyTravel` of it, the rope angle the rest.
 */
export function craneSwingLateral(
  config: HazardsConfig['craneSwing'],
  arrivalPhase: number,
  clock: number,
  out: CraneSwingLateral = { angle: 0, pivotX: 0 },
): CraneSwingLateral {
  const s = Math.sin((TAU * clock) / config.swingPeriod + arrivalPhase);
  out.pivotX = config.trolleyTravel * s;
  out.angle = Math.asin(((config.reach - config.trolleyTravel) * s) / craneSwingRadius(config.rig));
  return out;
}

/**
 * Collider of the swinging load: the block turned about its rope (spin), tilted with the rope sideways
 * (lateral) and along the track (swing) about the rope top – its bounding box, plus the sling above it
 * (no jumping over). Same rotation order as the presenter: spin first, then the lateral tilt, then the
 * tilt along the track.
 */
export function craneSwingBox(rig: CraneLoadRig, z: number, pose: CraneSwingPose, slot: HazardCollider): HazardCollider {
  const radius = craneSwingRadius(rig);
  const pivotY = pose.hoist - rig.loadBottom + rig.pivotHeight;
  const ct = Math.cos(pose.lateral);
  const st = Math.sin(pose.lateral);
  const cp = Math.cos(pose.swing);
  const sp = Math.sin(pose.swing);
  const cs = Math.cos(pose.spin);
  const ss = Math.sin(pose.spin);
  // R = R_along · R_lateral · R_spin with R_spin = [[cs, 0, ss], [0, 1, 0], [−ss, 0, cs]],
  // R_lateral = [[ct, −st, 0], [st, ct, 0], [0, 0, 1]], R_along = [[1, 0, 0], [0, cp, sp], [0, −sp, cp]]
  // (a positive swing moves the hanging load towards +z, a positive lateral angle towards +x).
  const r00 = ct * cs;
  const r01 = -st;
  const r02 = ct * ss;
  const r10 = cp * st * cs - sp * ss;
  const r11 = cp * ct;
  const r12 = cp * st * ss + sp * cs;
  const r20 = -sp * st * cs - cp * ss;
  const r21 = -sp * ct;
  const r22 = -sp * st * ss + cp * cs;
  const hx = rig.load.width / 2;
  const hy = rig.load.height / 2;
  const hz = rig.load.depth / 2;
  const halfX = Math.abs(r00) * hx + Math.abs(r01) * hy + Math.abs(r02) * hz;
  const halfY = Math.abs(r10) * hx + Math.abs(r11) * hy + Math.abs(r12) * hz;
  const halfZ = Math.abs(r20) * hx + Math.abs(r21) * hy + Math.abs(r22) * hz;
  // Centre = rope top + R · (0, −radius, 0).
  const x = pose.pivotX - r01 * radius;
  const y = pivotY - r11 * radius;
  const zc = z - r21 * radius;
  setBox(slot, x - halfX, x + halfX, y - halfY, y + halfY + rig.guardHeight, zc - halfZ, zc + halfZ);
  return slot;
}

/**
 * OBS_CRANE_LOAD_B as a pendulum. The tower crane beside the track holds the block high over the track
 * centre, gently swaying and turning. After the warning it lowers it to knee height while working up a
 * swing across all three lanes (trolley and rope); from then on it is a heavy pendulum: slow at the
 * turning points over the outer lanes, fast over the centre lane, slightly twisting. Too low to slide
 * under, the sling above too high to jump over – the runner has to be in a lane the load is not over.
 *
 * The swing is phase-locked to the runner's arrival when the warning starts (ETA at the current speed) and
 * kept on it while the run accelerates (`relock`): the load is over the definition's lane right then. A
 * runner held up by a hit is not waited for – it meets the swing in another phase, and at every phase one
 * lane is free (unit test).
 */
export class CraneSwingModel implements HazardModel<CraneSwingPose> {
  readonly kind = HazardKind.CraneSwing;
  phase: HazardPhase = HazardPhase.Idle;
  readonly pose: CraneSwingPose;
  readonly previous: CraneSwingPose;
  readonly colliders = [colliderSlot()];
  private readonly arrivalPhase: number;
  private readonly lateral: CraneSwingLateral = { angle: 0, pivotX: 0 };
  private time = 0;
  /** Seconds since the warning started. */
  private sinceWarning = 0;
  /** Swing clock: seconds relative to the runner's arrival, locked when the warning starts. */
  private clock = 0;
  /** Turn when the lowering started – it eases into line with the track while coming down. */
  private spinAtLower = 0;
  /** Turning points passed on the swing clock – the chain creaks at each new one. */
  private turns = 0;

  /** Seconds before the runner's arrival at which the warning starts. */
  static startLead(config: HazardsConfig['craneSwing']): number {
    return config.telegraph + config.lowerDuration + config.readyLead;
  }

  constructor(
    private readonly spec: CraneSwingHazard,
    private readonly config: HazardsConfig['craneSwing'],
    world: WorldConfig,
  ) {
    this.arrivalPhase = craneSwingArrivalPhase(laneCenterX(world, spec.lane), spec.sweep);
    this.pose = craneSwingPose(config.hoistedClearance);
    this.previous = craneSwingPose(config.hoistedClearance);
    craneSwingBox(config.rig, spec.z, this.pose, this.colliders[0]!);
  }

  /** Seconds relative to the runner's arrival the swing is locked to (0 before the warning). */
  get swingClock(): number {
    return this.clock;
  }

  step(dt: number, ctx: HazardStepContext, cues: HazardCue[]): void {
    Object.assign(this.previous, this.pose);
    const c = this.config;
    this.time += dt;
    if (this.phase === HazardPhase.Idle) {
      if (ctx.armed && ctx.eta(this.spec.z) <= CraneSwingModel.startLead(c)) {
        this.phase = HazardPhase.Warning;
        this.sinceWarning = 0;
        // Swing clock 0 = runner arrives: the load is over the definition's lane right then.
        this.clock = -ctx.eta(this.spec.z);
        this.turns = this.turningPoints();
        cues.push(HazardCue.Warning);
      }
    } else {
      this.sinceWarning += dt;
      this.clock += dt;
      // The run accelerates: creep towards the arrival the runner is heading for now – never after a held-up one.
      const drift = -ctx.eta(this.spec.z) - this.clock;
      if (Math.abs(drift) < c.relock.window) this.clock += clamp(drift, -c.relock.rate * dt, c.relock.rate * dt);
      if (this.phase === HazardPhase.Warning && this.sinceWarning >= c.telegraph) {
        this.phase = HazardPhase.Active;
        this.spinAtLower = this.pose.spin;
      }
    }

    const lowerTime = this.phase === HazardPhase.Active ? this.sinceWarning - c.telegraph : 0;
    const lowered = smoothstep(lowerTime / c.lowerDuration);
    // The crane works the swing up from the warning on; it is full when the load hangs low.
    const build = this.phase === HazardPhase.Idle ? 0 : smoothstep(this.sinceWarning / (c.telegraph + c.lowerDuration));
    const full = craneSwingLateral(c, this.arrivalPhase, this.clock, this.lateral);
    const idleSway = c.idleSwayDegrees * DEG_TO_RAD * Math.sin((TAU * this.time) / c.idleSwayPeriod);
    this.pose.lateral = lerp(idleSway, full.angle, build);
    this.pose.pivotX = full.pivotX * build;
    // The rope stretches when the lowering stops: a short dip, a small overshoot, settled.
    const rebound = (lowerTime - c.lowerDuration) / c.rebound.duration;
    const dip = rebound > 0 && rebound < 1 ? c.rebound.height * Math.sin(TAU * rebound) * (1 - rebound) : 0;
    this.pose.hoist = lerp(c.hoistedClearance, c.hangClearance, lowered) - dip;
    this.pose.swing = c.wobbleDegrees * DEG_TO_RAD * Math.sin((TAU * this.time) / c.wobblePeriod);
    if (this.phase === HazardPhase.Active) {
      const aligned = Math.round(this.spinAtLower / Math.PI) * Math.PI;
      const twist = c.twistDegrees * DEG_TO_RAD * Math.sin((TAU * this.time) / c.twistPeriod);
      this.pose.spin = lerp(this.spinAtLower, aligned, lowered) + twist * lowered;
    } else {
      this.pose.spin += c.idleSpinDegreesPerSecond * DEG_TO_RAD * dt;
    }
    craneSwingBox(c.rig, this.spec.z, this.pose, this.colliders[0]!);

    // The chain creaks at every turning point until the runner is past the block (a turn on arrival included).
    const turns = this.turningPoints();
    if (this.phase === HazardPhase.Active && turns !== this.turns && ctx.eta(this.spec.z + c.rig.load.depth) > 0) cues.push(HazardCue.Creak);
    this.turns = turns;
  }

  /** Turning points (load centre at ±reach) the swing clock has passed – they lie at phase π/2 + n·π. */
  private turningPoints(): number {
    return Math.floor(((TAU * this.clock) / this.config.swingPeriod + this.arrivalPhase - Math.PI / 2) / Math.PI);
  }
}
